/**
 * Where you are, and how to get back.
 *
 * The console had a navigation rail and nothing else: opening a list from Settings
 * left you with no way back except finding the rail again, the topbar showed only the
 * company name, two rail items lit up at once on a nested route, and every page had
 * the same browser title so history and the browser's own back button were useless.
 *
 * One trail, computed from the URL, drives all four: the crumbs in the topbar, the
 * back link on a sub-page, which rail item is lit, and the document title. A page with
 * something in the URL that only it can name — a collection code, a settings list —
 * fills in its own last crumb with `usePageCrumb`.
 */
import {
  createContext, useContext, useEffect, useMemo, useState, type ReactNode,
} from 'react'
import { Link, useLocation } from 'react-router-dom'
import { IconChevron } from '../ui/kit'

export interface Crumb {
  label: string
  /** Absent on the last crumb — you are already there. */
  to?: string
}

/** Every console route, its name, and its parent. One place, so nothing drifts. */
const PAGES: Record<string, { label: string; parent?: string }> = {
  '/console': { label: 'Today' },
  '/console/collections': { label: 'Collections' },
  '/console/logistics': { label: 'Deliveries' },
  '/console/suppliers': { label: 'Farmers' },
  '/console/offers': { label: 'Farmer offers' },
  '/console/pricing': { label: 'Prices' },
  '/console/buyers': { label: 'Buyers' },
  '/console/orders': { label: 'Orders' },
  '/console/payouts': { label: 'Paying farmers' },
  '/console/finance': { label: 'Invoices' },
  '/console/reports': { label: 'Profit per kilo' },
  '/console/setup': { label: 'Settings & lists' },
}

/** The deepest declared page that this path sits under. */
export function pageFor(pathname: string): string {
  const clean = pathname.replace(/\/+$/, '') || '/console'
  if (PAGES[clean]) return clean

  let best = '/console'
  for (const key of Object.keys(PAGES)) {
    if (key !== '/console' && clean.startsWith(key + '/') && key.length > best.length) {
      best = key
    }
  }
  return best
}

/**
 * Which rail item should be lit. Exactly one: the longest declared route this path
 * falls under. `/console/setup/expenses` lights Settings & lists, not both it and
 * whichever other item happens to point inside it.
 */
export function activeNavFor(pathname: string, navTargets: string[]): string | null {
  const clean = pathname.replace(/\/+$/, '') || '/console'
  let best: string | null = null
  for (const to of navTargets) {
    if (clean === to || clean.startsWith(to + '/')) {
      if (!best || to.length > best.length) best = to
    }
  }
  // A rail item pointing at a path nobody is on should not light up because it is a
  // prefix of nothing; '/console' would otherwise match everything.
  if (best === '/console' && clean !== '/console') return null
  return best
}

// ---------------------------------------------------------------- the tail crumb
interface TailState {
  tail: Crumb[]
  setTail: (c: Crumb[]) => void
}

const TailContext = createContext<TailState>({ tail: [], setTail: () => {} })

export function BreadcrumbProvider({ children }: { children: ReactNode }) {
  const [tail, setTail] = useState<Crumb[]>([])
  const value = useMemo(() => ({ tail, setTail }), [tail])
  return <TailContext.Provider value={value}>{children}</TailContext.Provider>
}

/**
 * For a page whose name is only known once its data loads — a collection's code, the
 * settings list you opened. Pass an empty array while it is still loading.
 */
export function usePageCrumb(crumbs: Crumb[]) {
  const { setTail } = useContext(TailContext)
  const key = crumbs.map((c) => `${c.label}|${c.to ?? ''}`).join('>')

  useEffect(() => {
    setTail(crumbs)
    return () => setTail([])
    // Compared by content: a page that rebuilds the same array every render would
    // otherwise loop forever.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key])
}

/** The full trail for the current location. */
export function useCrumbs(): Crumb[] {
  const { pathname } = useLocation()
  const { tail } = useContext(TailContext)

  return useMemo(() => {
    const page = pageFor(pathname)
    const trail: Crumb[] = []

    if (page !== '/console') trail.push({ label: 'Today', to: '/console' })

    const def = PAGES[page]
    if (def) {
      // A page with a tail is no longer the destination, so it becomes a link.
      trail.push({ label: def.label, to: tail.length > 0 ? page : undefined })
    }

    return [...trail, ...tail]
  }, [pathname, tail])
}

// ---------------------------------------------------------------- rendering
export function Breadcrumbs() {
  const crumbs = useCrumbs()

  // Keep the browser tab and history honest, so the browser's own back button and the
  // history menu are usable.
  useEffect(() => {
    const here = crumbs[crumbs.length - 1]?.label
    document.title = here ? `${here} · Ledger` : 'Ledger'
  }, [crumbs])

  if (crumbs.length === 0) return null

  return (
    <nav className="crumbs" aria-label="Breadcrumb">
      {crumbs.map((c, i) => (
        <span key={`${c.label}-${i}`} className="crumb-item">
          {i > 0 && <IconChevron size={12} />}
          {c.to
            ? <Link to={c.to}>{c.label}</Link>
            : <b aria-current="page">{c.label}</b>}
        </span>
      ))}
    </nav>
  )
}

/**
 * The way out of a sub-page. A real link to the parent rather than history.back():
 * somebody who opened this from a link, a bookmark or a refresh has no history to go
 * back through, and "back" would take them out of the app.
 */
export function BackLink({ to, label }: { to: string; label: string }) {
  return (
    <Link className="back-link" to={to}>
      <IconChevron dir="left" size={14} />
      {label}
    </Link>
  )
}
